import React, { useCallback, useEffect, useState } from "react";
import { fxApi } from "@/api/fxClient";

/**
 * CareLogix™ ORACLE — LOGIX-2 FX Trade Intelligence overlay (Phase 22B).
 *
 * Deterministic synthetic intelligence (Direction / Confidence / Risk /
 * Entry / Target / Stop / Thesis). Research-only with HITL flag.
 *
 * Props:
 *   instrument: "GBP_USD" (default)
 *   timeframe:  "5M"      (default)
 *   compact:    boolean   — hide drivers/risks lists when true
 */
export default function Logix2FxIntelligence({
  instrument = "GBP_USD",
  timeframe = "5M",
  compact = false,
} = {}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setBusy(true);
    setErr("");
    try {
      const { data: d } = await fxApi.intelligence(instrument, timeframe);
      setData(d);
    } catch (e) {
      setErr(e?.response?.data?.detail?.error || e?.message || "intel_failed");
    } finally {
      setBusy(false);
    }
  }, [instrument, timeframe]);

  useEffect(() => { load(); }, [load]);

  if (!data) {
    return (
      <section className="fx-intel" data-testid="fx-intel-panel">
        <header className="fx-intel__head">
          <span className="fx-intel__title">⚡ LOGIX-2 FX Intelligence</span>
        </header>
        <p className="fx-discl">{busy ? "Loading LOGIX-2 intelligence…" : (err || "—")}</p>
      </section>
    );
  }

  const conf = Math.max(0, Math.min(100, data.confidence ?? 0));
  const risk = Math.max(0, Math.min(100, data.risk ?? 0));
  const dirClass = (data.direction || "neutral").toLowerCase();
  const isBuy = data.direction === "bullish";

  return (
    <section className="fx-intel" data-testid="fx-intel-panel">
      <header className="fx-intel__head">
        <span className="fx-intel__title">⚡ LOGIX-2 FX Intelligence · {data.pair} · {data.timeframe}</span>
        <span className={`fx-intel__dir-pill ${dirClass}`} data-testid="fx-intel-direction">{data.direction}</span>
      </header>

      <div className="fx-intel__scores">
        <div className="fx-intel__score">
          <span className="fx-intel__score-label">Confidence</span>
          <span className="fx-intel__score-val" data-testid="fx-intel-confidence">{conf}%</span>
          <div className="fx-intel__bar"><div className="fx-intel__bar-fill" style={{ width: `${conf}%` }} /></div>
        </div>
        <div className="fx-intel__score">
          <span className="fx-intel__score-label">Risk Score</span>
          <span className="fx-intel__score-val" data-testid="fx-intel-risk">{risk}%</span>
          <div className="fx-intel__bar"><div className="fx-intel__bar-fill risk" style={{ width: `${risk}%` }} /></div>
        </div>
      </div>

      <div className="fx-intel__targets">
        <div className="fx-intel__target">
          <span className="fx-intel__target-label">Entry</span>
          <span className={`fx-intel__target-val ${isBuy ? "is-buy" : "is-sell"}`} data-testid="fx-intel-entry">{data.entry}</span>
        </div>
        <div className="fx-intel__target">
          <span className="fx-intel__target-label">Target</span>
          <span className="fx-intel__target-val is-buy" data-testid="fx-intel-target">{data.target}</span>
        </div>
        <div className="fx-intel__target">
          <span className="fx-intel__target-label">Stop</span>
          <span className="fx-intel__target-val is-sell" data-testid="fx-intel-stop">{data.stop}</span>
        </div>
      </div>

      <p className="fx-intel__thesis" data-testid="fx-intel-thesis">{data.thesis}</p>

      {!compact && (
        <div className="fx-intel__lists">
          <div className="fx-intel__list">
            <h4>Confidence Drivers</h4>
            <ul data-testid="fx-intel-drivers">
              {(data.drivers || []).map((d, i) => <li key={`d-${i}`}>{d}</li>)}
            </ul>
          </div>
          <div className="fx-intel__list">
            <h4>Risks</h4>
            <ul data-testid="fx-intel-risks">
              {(data.risks || []).map((r, i) => <li key={`r-${i}`}>{r}</li>)}
            </ul>
          </div>
        </div>
      )}

      <p className="fx-discl">
        Reward/Risk ≈ <strong>{data.reward_risk_ratio}x</strong>. {data.disclaimer}
      </p>
    </section>
  );
}
